// src/pages/WarrantyApp.jsx
import React, { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { Outlet } from "react-router-dom";

import AppHeader from "../Layout/AppHeader";
import BottomNav from "./components/warranty/BottomNav";

import ClaimWizardDialog from "./components/dialogs/ClaimWizardDialog";
import AddressDialog from "./components/dialogs/AddressDialog";
import PurchasesDialog from "./components/dialogs/PurchasesDialog";
import DeviceDetailsDialog from "./components/dialogs/DeviceDetailsDialog";

import { AuthContext } from "./context/AuthContext";
import { BASE_URL } from "./config/api";
import axios from "axios";
import ConfirmDialog from "./components/dialogs/ConfirmDialog";
import ClaimDetailsDialog from "./components/dialogs/ClaimDetailsDialog";

export default function WarrantyApp() {
  const { user, logout } = useContext(AuthContext);
  const customerId = user?.id;

  /* ---------------- GLOBAL UI STATE ---------------- */

  const [bootLoading, setBootLoading] = useState(true);

  const [openClaim, setOpenClaim] = useState(false);
  const [preselectedDeviceId, setPreselectedDeviceId] = useState(null);

  const [addresses, setAddresses] = useState([]);
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [addressMode, setAddressMode] = useState("add");
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [savingAddress, setSavingAddress] = useState(false);

  const [openPurchases, setOpenPurchases] = useState(false);
  const [activePurchaseId, setActivePurchaseId] = useState(null);

  const [openDeviceDetails, setOpenDeviceDetails] = useState(false);
  const [activeDeviceId, setActiveDeviceId] = useState(null);

  const [activeClaim, setActiveClaim] = useState(null);

  const [deleteAddressId, setDeleteAddressId] = useState(null);
  const [deleting, setDeleting] = useState(false);


  const [customers, setCustomers] = useState([]);   // full customer list
  const [customerInfo, setCustomerInfo] = useState(null); // logged-in customer
  const [retailerInfo, setRetailerInfo] = useState(null); // retailer details

  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  const [claims, setClaims] = useState([]);
  const [claimsMeta, setClaimsMeta] = useState(null);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [openClaimDetails, setOpenClaimDetails] = useState(false);

  const globalLoading =
  bootLoading || devicesLoading || claimsLoading;

  const purchases = [
    {
      id: "pur-1",
      model_name: "Google Pixel 8",
      purchase_date: "2025-12-20",
      order_no: "ORD-77881",
      serial_no: "SN-PXL8-88991234",
      imei: "356789101112131",
      warranty_expiry: "2026-12-20",
      purchased_from: { name: "Akash Mobile World" },
    },
    {
      id: "pur-2",
      model_name: "Samsung Galaxy S24",
      purchase_date: "2025-11-08",
      order_no: "ORD-66420",
      serial_no: "SN-S24-10012025",
      imei: "357891011121314",
      warranty_expiry: "2026-11-08",
      purchased_from: { name: "City Mobiles" },
    },
  ];
  /* ---------------- DERIVED DATA ---------------- */

  const fetchClaims = async () => {
    if (!customerId) return;

    try {
      setClaimsLoading(true);

      const res = await axios.get(
        `${BASE_URL}warranty/claims/list`,
        {
          params: { w_customer_id: customerId },
        }
      );

      if (res.data?.status) {
        const payload = res.data.data;

        setClaims(payload.data || []);
        setClaimsMeta({
          currentPage: payload.current_page,
          lastPage: payload.last_page,
          total: payload.total,
          perPage: payload.per_page,
        });
      } else {
        setClaims([]);
        setClaimsMeta(null);
      }
    } catch (err) {
      console.error(
        "Failed to fetch claims",
        err?.response?.data || err.message
      );
      setClaims([]);
      setClaimsMeta(null);
    } finally {
      setClaimsLoading(false);
    }
  };

  const claimRecords = claims;

  const activeDevice = devices.find((d) => d.id === activeDeviceId);
  const activeDevicePurchase = purchases.find(
    (p) => p.id === activeDevice?.purchase_id
  );

  const deviceClaims = claimRecords.filter(
    (c) => Number(c.w_device_id) === Number(activeDeviceId)
  );

//   const activeClaim = claims.find(
//   (c) => Number(c.id) === Number(activeClaimId)
// );

const activeClaimDevice = devices.find(
  (d) => Number(d.id) === Number(activeClaim?.w_device_id || activeClaim?.device_id)
);

const activeClaimPurchase = purchases.find(
  (p) => Number(p.id) === Number(activeClaimDevice?.purchase_id)
);

const normalizeClaim = (claim) => ({
  ...claim,

  // 🔹 convert issue_description → issues[]
  issues: claim.issue_description
    ? claim.issue_description.split(",").map(s => s.trim())
    : [],

  // 🔹 fake timeline from status
  status_history: [
    {
      key: claim.status,
      at: claim.updated_at,
    },
  ],

  // 🔹 no payment yet
  payment: null,
});



  /* ---------------- ACTIONS ---------------- */

  const startClaim = (deviceId) => {
  if (deviceId) {
    setPreselectedDeviceId(deviceId);
  } else {
    setPreselectedDeviceId(null);
  }
  setOpenClaim(true);
};


  const openAddAddress = () => {
    setAddressMode("add");
    setSelectedAddress(null);
    setOpenAddressDialog(true);
  };

  const openEditAddress = (address) => {
    setAddressMode("edit");
    setSelectedAddress(address);
    setOpenAddressDialog(true);
  };

  const openPurchaseList = (purchaseId = null) => {
    setActivePurchaseId(purchaseId);
    setOpenPurchases(true);
  };

  const openDeviceDetailsById = (id) => {
    setActiveDeviceId(id);
    setOpenDeviceDetails(true);
  };

  const handleOpenClaimDetails = (claim) => {
    setActiveClaim(normalizeClaim(claim));
    setOpenClaimDetails(true);
  };

  const fetchAddresses = async () => {
  if (!customerId) return;

  try {
    const res = await axios.post(
      `${BASE_URL}warranty/customer/address/list`,
      {
        w_customer_id: customerId,
      }
    );

    if (res.data?.status) {
      setAddresses(res.data.data || []);
    }
  } catch (err) {
    console.error(
      "Failed to fetch addresses",
      err?.response?.data || err.message
    );
  }
};

  const fetchCustomerDevices = async () => {
  if (!customerId) return;

  try {
    setDevicesLoading(true);

    const res = await axios.get(
      `${BASE_URL}warranty/get-customer-details`,
      { params: { id: customerId } }
    );

    const data = res.data;

    // ✅ CASE 1: API returns SINGLE CUSTOMER OBJECT
    if (data && !Array.isArray(data)) {
      setCustomerInfo(data);
      setRetailerInfo(data.retailer || null);
      setDevices(data.devices || []);
      return;
    }

    // ✅ CASE 2: API returns ARRAY of customers
    if (Array.isArray(data)) {
      setCustomers(data);

      const currentCustomer = data.find(
        (c) => Number(c.id) === Number(customerId)
      );

      setCustomerInfo(currentCustomer || null);
      setRetailerInfo(currentCustomer?.retailer || null);
      setDevices(currentCustomer?.devices || []);
      return;
    }

    // ❌ fallback
    setCustomerInfo(null);
    setRetailerInfo(null);
    setDevices([]);
  } catch (err) {
    console.error(
      "Failed to fetch customer details",
      err?.response?.data || err.message
    );
    setCustomerInfo(null);
    setRetailerInfo(null);
    setDevices([]);
  } finally {
    setDevicesLoading(false);
  }
};


  const handleSaveAddress = async (payload) => {
  try {
    setSavingAddress(true);

    if (addressMode === "add") {
      // 🔹 CREATE
      const res = await axios.post(
        `${BASE_URL}warranty/customer/address/create`,
        {
          w_customer_id: customerId,
          ...payload,
        }
      );

      if (res.data?.status) {
        await fetchAddresses(); // 🔥 refresh from server
        setOpenAddressDialog(false);
      }
    } else {
      // 🔹 UPDATE
      const res = await axios.post(
        `${BASE_URL}warranty/customer/address/update`,
        {
          address_id: selectedAddress.id,
          ...payload,
        }
      );

      if (res.data?.status) {
        setAddresses((prev) =>
          prev.map((a) =>
            a.id === selectedAddress.id
              ? { ...a, ...payload }
              : a
          )
        );
        setOpenAddressDialog(false);
      }
    }
  } catch (err) {
    alert(
      err?.response?.data?.message ||
        "Failed to save address"
    );
  } finally {
    setSavingAddress(false);
  }
};


  const confirmDeleteAddress = (id) => {
  setDeleteAddressId(id);
};

const handleDeleteAddress = async () => {
  if (!deleteAddressId) return;

  try {
    setDeleting(true);

    await axios.post(
      `${BASE_URL}warranty/customer/address/delete`,
      { address_id: deleteAddressId }
    );

    await fetchAddresses(); // refresh list
    setDeleteAddressId(null);
  } catch (err) {
    alert("Failed to delete address");
  } finally {
    setDeleting(false);
  }
};




  /* ---------------- BOOT ---------------- */

  useEffect(() => {
  if (!customerId) return;

  fetchAddresses();
  fetchCustomerDevices();
  fetchClaims(); // ✅ ADD THIS

  const t = setTimeout(() => setBootLoading(false), 500);
  return () => clearTimeout(t);
}, [customerId]);

  


  /* ---------------- RENDER ---------------- */

  if (!user) return null;

  return (
    <>
    {globalLoading && <GlobalLoader />}
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f4f6fb",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* HEADER */}
      <AppHeader user={user} onLogout={logout} />

      {/* ROUTED CONTENT */}
      <Box sx={{ flex: 1, p: 2,overflowY: "auto", pb: "72px", }}>
        <Outlet
          context={{
            bootLoading,
            devices,
            claims,
            claimsLoading,        // ✅ ADD
            customerInfo,
            retailerInfo,
            addresses,
            onRaiseClaim: startClaim,
            onOpenClaimDetails: handleOpenClaimDetails, // ✅ ADD
            onOpenDevice: openDeviceDetailsById,
            onViewPurchase: openPurchaseList,
            onAddAddress: openAddAddress,
            onEditAddress: openEditAddress,
            onDeleteAddress: confirmDeleteAddress,
          }}
        />


      </Box>

      {/* BOTTOM NAV (URL BASED) */}
      <BottomNav />

      {/* ---------------- DIALOGS ---------------- */}

      <ClaimWizardDialog
        open={openClaim}
        preselectedDeviceId={preselectedDeviceId}
        onClose={() => setOpenClaim(false)}
        devices={devices}
        addresses={addresses}
        retailerInfo={retailerInfo}
        channelPartners={[]}
        onSubmit={async () => {
          setOpenClaim(false);
          await fetchClaims(); // 🔥 REFRESH CLAIM LIST
        }}
      />

      <AddressDialog
        open={openAddressDialog}
        mode={addressMode}
        initialData={selectedAddress}
        saving={savingAddress}
        onClose={() => setOpenAddressDialog(false)}
        onSave={handleSaveAddress}
      />

      <PurchasesDialog
        open={openPurchases}
        onClose={() => setOpenPurchases(false)}
        purchases={devices}
        retailerInfo={retailerInfo}
        activePurchaseId={activePurchaseId}
      />

      <DeviceDetailsDialog
        open={openDeviceDetails}
        onClose={() => setOpenDeviceDetails(false)}
        device={activeDevice}
        retailerInfo={retailerInfo} 
        deviceClaims={deviceClaims}
        onRaiseClaim={(id) => {
          setOpenDeviceDetails(false);
          startClaim(id);
        }}
        onOpenClaim={(claim) => {
          setOpenDeviceDetails(false);
          handleOpenClaimDetails(claim); // ✅ FIXED
        }}

      />

      <ConfirmDialog
        open={Boolean(deleteAddressId)}
        title="Delete Address?"
        description="This address will be permanently removed. This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        onClose={() => setDeleteAddressId(null)}
        onConfirm={handleDeleteAddress}
      />

      <ClaimDetailsDialog
        open={openClaimDetails}
        onClose={() => setOpenClaimDetails(false)}
        claim={activeClaim}
        device={activeClaim?.device}   // ✅ API already sends this
      />
    </Box>

    </>
  );
}


function GlobalLoader() {
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        bgcolor: "rgba(255,255,255,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(2px)",
      }}
    >
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
        <span className="text-sm text-slate-600 font-medium">
          Loading, please wait…
        </span>
      </div>
    </Box>
  );
}

  


// // src/WarrantyApp.jsx
// import React, { useContext, useEffect, useState } from "react";
// import { Box } from "@mui/material";
// import LoginScreen from "./CustomerLogin";
// import AppHeader from "../Layout/AppHeader";
// import HomeTab from "./components/warranty/HomeTab";
// import DevicesTab from "./components/warranty/DevicesTab";
// import ClaimsTab from "./components/warranty/ClaimsTab";
// import ProfileTab from "./components/warranty/ProfileTab";
// import BottomNav from "./components/warranty/BottomNav";
// import ClaimWizardDialog from "./components/dialogs/ClaimWizardDialog";
// import AddressDialog from "./components/dialogs/AddressDialog";
// import PurchasesDialog from "./components/dialogs/PurchasesDialog";
// import { AuthContext } from "./context/AuthContext";
// import DeviceDetailsDialog from "./components/dialogs/DeviceDetailsDialog";


// export default function WarrantyApp() {
//  const { user, logout  } = useContext(AuthContext);
//   const [tab, setTab] = useState(0);
//   const [openClaim, setOpenClaim] = useState(false);
//   const [preselectedDeviceId, setPreselectedDeviceId] = useState(null);
//   const [openAddressDialog, setOpenAddressDialog] = useState(false);
//   const [addressMode, setAddressMode] = useState("add"); // add | edit
//   const [selectedAddress, setSelectedAddress] = useState(null);
//   const [savingAddress, setSavingAddress] = useState(false);
//   const [openPurchases, setOpenPurchases] = useState(false);
//   const [activePurchaseId, setActivePurchaseId] = useState(null);
//   const [openDeviceDetails, setOpenDeviceDetails] = useState(false);
//   const [activeDeviceId, setActiveDeviceId] = useState(null);
//   const [openClaimDetails, setOpenClaimDetails] = useState(false);
//   const [activeClaimId, setActiveClaimId] = useState(null);



//   // 🔹 ADDRESSES (mock for now)
// const [addresses, setAddresses] = useState([
//   {
//     id: "local-1",
//     name: "Rohit Sharma",
//     mobile: "9876543210",
//     address1: "Flat 301, ABC Apartment",
//     address2: "Near City Mall",
//     city: "Pune",
//     state: "Maharashtra",
//     pincode: "411001",
//     lat: 18.5204,
//     lng: 73.8567,
//   },
// ]);

// // 🔹 DEVICES (mock for now)
// const devices = [
//   {
//     id: "dev-1",
//     name: "Google Pixel 8",
//     plan: "Premium Protection",
//     status: "Warranty Active",
//     statusColor: "success",
//     purchase_id: "pur-1",
//     cover_amount: 60000,
//     cover_balance: 60000,
//     claim_balance: 3,
//     claims_done: 1,
//   },
//   {
//     id: "dev-2",
//     name: "Samsung Galaxy S24",
//     plan: "Screen + Extended",
//     status: "Warranty Active",
//     statusColor: "success",
//     purchase_id: "pur-2",
//     cover_amount: 80000,
//     cover_balance: 78500,
//     claim_balance: 2,
//     claims_done: 1,
//   },
// ];


// const purchases = [
//     {
//       id: "pur-1",
//       model_name: "Google Pixel 8",
//       purchase_date: "2025-12-20",
//       order_no: "ORD-77881",
//       serial_no: "SN-PXL8-88991234",
//       imei: "356789101112131",
//       warranty_expiry: "2026-12-20",
//       purchased_from: {
//         name: "Akash Mobile World",
//       },
//     },
//     {
//       id: "pur-2",
//       model_name: "Samsung Galaxy S24",
//       purchase_date: "2025-11-08",
//       order_no: "ORD-66420",
//       serial_no: "SN-S24-10012025",
//       imei: "357891011121314",
//       warranty_expiry: "2026-11-08",
//       purchased_from: {
//         name: "City Mobiles",
//       },
//     },
//   ];

//   const [claimRecords, setClaimRecords] = useState([
//     {
//       id: "CLM-20251230-0001",
//       device_id: "dev-1",
//       purchase_id: "pur-1",
//       issues: ["Screen Damage"],
//       notes: "Top-right corner crack.",
//       photos_count: 2,
//       service: "pickup",
//       pickup_address_id: "local-1",
//       drop_partner_id: "",
//       created_at: "2025-12-30T10:10:00.000Z",
//       approval: { status: "pending", at: null, remark: "" },
//       payment: { required: false, amount: 0, status: "none", requested_at: null, paid_at: null },
//       status_history: [
//         { key: "raised", label: "Claim Raised", color: "success", at: "2025-12-30T10:10:00.000Z" },
//         { key: "scheduled", label: "Pickup Scheduled", color: "info", at: "2025-12-30T12:30:00.000Z" },
//       ],
//     },
//     {
//       id: "CLM-20251231-0007",
//       device_id: "dev-2",
//       purchase_id: "pur-2",
//       issues: ["Liquid Damage"],
//       notes: "Service team said inspection fee required.",
//       photos_count: 1,
//       service: "drop",
//       pickup_address_id: "",
//       drop_partner_id: "cp-2",
//       created_at: "2025-12-31T07:05:00.000Z",
//       approval: { status: "pending", at: null, remark: "" },
//       payment: { required: true, amount: 299, status: "requested", requested_at: "2025-12-31T07:30:00.000Z", paid_at: null },
//       status_history: [
//         { key: "raised", label: "Claim Raised", color: "success", at: "2025-12-31T07:05:00.000Z" },
//         { key: "payment_requested", label: "Payment Requested", color: "warning", at: "2025-12-31T07:30:00.000Z" },
//       ],
//     },
//   ]);


//   // Fake boot loader
//   const [bootLoading, setBootLoading] = useState(true);

//   const startClaim = (deviceId = null) => {
//   setPreselectedDeviceId(deviceId);
//   setOpenClaim(true);
// };

// const openAddDialog = () => {
//   setAddressMode("add");
//   setSelectedAddress(null);
//   setOpenAddressDialog(true);
// };

// const openEditDialog = (address) => {
//   setAddressMode("edit");
//   setSelectedAddress(address);
//   setOpenAddressDialog(true);
// };

// const openPurchaseList = (purchaseId = null) => {
//   setActivePurchaseId(purchaseId);
//   setOpenPurchases(true);
// };

// function openDeviceDetailsById(id) {
//     setActiveDeviceId(id);
//     setOpenDeviceDetails(true);
//   }

//   function openClaimDetailsById(id) {
//     setActiveClaimId(id);
//     setOpenClaimDetails(true);
//   }




// const handleSaveAddress = async (payload) => {
//   try {
//     setSavingAddress(true);

//     if (addressMode === "add") {
//       // 🔥 API CALL HERE (or local add)
//       setAddresses((prev) => [
//         { id: `local-${Date.now()}`, ...payload },
//         ...prev,
//       ]);
//     } else {
//       setAddresses((prev) =>
//         prev.map((a) =>
//           a.id === selectedAddress.id ? { ...a, ...payload } : a
//         )
//       );
//     }

//     setOpenAddressDialog(false);
//   } catch (err) {
//     console.error(err);
//   } finally {
//     setSavingAddress(false);
//   }
// };

// const activeDevice = devices.find(d => d.id === activeDeviceId);
// const activeDevicePurchase = purchases.find(
//   p => p.id === activeDevice?.purchase_id
// );
// const deviceClaims = claimRecords.filter(
//   c => c.device_id === activeDeviceId
// );



//   useEffect(() => {
//     const t = setTimeout(() => setBootLoading(false), 600);
//     return () => clearTimeout(t);
//   }, []);

  
//   if (!user) {
//     return <LoginScreen />;
//   }

//   return (
//     <Box sx={{ minHeight: "100vh", bgcolor: "#f4f6fb", display: "flex", flexDirection: "column" }}>
//       <AppHeader user={user} onLogout={logout} />

//       <Box sx={{ flex: 1, p: 2 }}>
//         {tab === 0 && <HomeTab
//           bootLoading={bootLoading}
//           onRaiseClaim={() => startClaim()}   // 🔥 this opens the real dialog
          
//         />}
//         {tab === 1 && (
//          <DevicesTab
//           devices={devices}                 // ✅ PASS
//           purchases={purchases}             // ✅ PASS
//           onRaiseClaim={(id) => startClaim(id)}
//           onViewPurchase={(id) => openPurchaseList(id)}
//           onOpenDevice={(id) => openDeviceDetailsById(id)}
//         />

//         )}
//         {tab === 2 && (
//           <ClaimsTab
//             onRaiseClaim={() => startClaim()}
//           />
//         )}
//         {tab === 3 && (
//           <ProfileTab
//             user={user}
//             onAddAddress={openAddDialog}
//             onEditAddress={openEditDialog}
//             onViewAllPurchases={() => openPurchaseList(null)}
//           />

//         )}
//       </Box>

//       <BottomNav tab={tab} setTab={setTab} />

//       <ClaimWizardDialog
//         open={openClaim}
//         preselectedDeviceId={preselectedDeviceId}
//         onClose={() => setOpenClaim(false)}
//         onSubmit={(newClaim) => {
//           console.log("Claim submitted:", newClaim);
//           setOpenClaim(false);
//           setTab(2); // go to Claims tab
//         }}
//         devices={devices}          // ✅ ADD
//         addresses={addresses}      // ✅ ADD
//         channelPartners={[]}       // ✅ ADD (safe default)
//       />

//       <AddressDialog
//         open={openAddressDialog}
//         mode={addressMode}
//         initialData={selectedAddress}
//         saving={savingAddress}
//         onClose={() => setOpenAddressDialog(false)}
//         onSave={handleSaveAddress}
//       />

//       <PurchasesDialog
//         open={openPurchases}
//         onClose={() => setOpenPurchases(false)}
//         purchases={purchases}
//         activePurchaseId={activePurchaseId}
//       />

//       <DeviceDetailsDialog
//         open={openDeviceDetails}
//         onClose={() => setOpenDeviceDetails(false)}
//         device={activeDevice}
//         purchase={activeDevicePurchase}
//         deviceClaims={deviceClaims}
//         onRaiseClaim={(id) => {
//           setOpenDeviceDetails(false);
//           startClaim(id);
//         }}
//         onOpenClaim={(claimId) => {
//           setOpenDeviceDetails(false);
//           openClaimDetailsById(claimId);
//         }}
//       />


//     </Box>
//   );
// }




// // import { useEffect, useState } from "react";
// // import { useLocation, useNavigate } from "react-router-dom";
// // import HomeTab from "./components/warranty/HomeTab";
// // import TopBar from "./components/warranty/TopBar";
// // import DevicesTab from "./components/warranty/DevicesTab";
// // import ClaimsTab from "./components/warranty/ClaimsTab";
// // import ProfileTab from "./components/warranty/ProfileTab";
// // import BottomNav from "./components/warranty/BottomNav";


// // const TAB_PATHS = ["/", "/devices", "/claims", "/profile"];

// // export default function WarrantyApp() {
// //   const location = useLocation();
// //   const navigate = useNavigate();

// //   const [tab, setTab] = useState(0);

// //   // 🔄 Sync URL → tab
// //   useEffect(() => {
// //     const idx = TAB_PATHS.indexOf(location.pathname);
// //     if (idx >= 0) setTab(idx);
// //   }, [location.pathname]);

// //   // 🔄 Sync tab → URL
// //   useEffect(() => {
// //     navigate(TAB_PATHS[tab], { replace: true });
// //   }, [tab]);

// //   return (
// //     <div className="min-h-screen bg-slate-50 flex flex-col">
// //       <TopBar />

// //       <main className="flex-1 px-4 py-4">
// //         {tab === 0 && <HomeTab />}
// //         {tab === 1 && <DevicesTab />}
// //         {tab === 2 && <ClaimsTab />}
// //         {tab === 3 && <ProfileTab />}
// //       </main>

// //       <BottomNav tab={tab} setTab={setTab} />
// //     </div>
// //   );
// // }



// import React, { useMemo, useState } from "react";



// // You will add these next (same pattern as HomeTab):
// // import DevicesTab from "../components/warranty/DevicesTab";
// // import ClaimsTab from "../components/warranty/ClaimsTab";
// // import ProfileTab from "../components/warranty/ProfileTab";
// // import ClaimWizardModal from "../components/warranty/dialogs/ClaimWizardModal";
// // import ClaimDetailsModal from "../components/warranty/dialogs/ClaimDetailsModal";
// // import PurchasesModal from "../components/warranty/dialogs/PurchasesModal";
// // import AddressModal from "../components/warranty/dialogs/AddressModal";
// // import Toast from "../components/warranty/ui/Toast";
// import TopBar from "./components/warranty/TopBar";
// import BottomNav from "./components/warranty/BottomNav";
// import HomeTab from "./components/warranty/HomeTab";

// export default function WarrantyApp() {
//   const [tab, setTab] = useState(0);

//   // ======= Keep your same data (use your existing arrays from MUI code) =======
//   const [purchases] = useState([
//     // ...use your existing purchases array
//   ]);
//   const [devices] = useState([
//     // ...use your existing devices array
//   ]);
//   const [claimRecords] = useState([
//     // ...use your existing claim records array
//   ]);

//   // dialogs (wire them exactly like your MUI version)
//   const [openPurchases, setOpenPurchases] = useState(false);

//   const purchasesSorted = useMemo(() => {
//     return [...purchases].sort((a, b) => new Date(b.purchase_date).getTime() - new Date(a.purchase_date).getTime());
//   }, [purchases]);

//   const latestPurchase = purchasesSorted[0] || null;
//   const uniqueShopCount = useMemo(() => {
//     const set = new Set(purchases.map((p) => String(p?.purchased_from?.name || "").toLowerCase()));
//     return set.size;
//   }, [purchases]);

//   const deviceCount = devices.length;
//   const claimCount = claimRecords.length;

//   function openPurchaseList() {
//     setOpenPurchases(true);
//   }

//   function startClaim() {
//     // open your claim wizard modal (same as MUI)
//     // setOpenClaim(true)
//   }

//   return (
//     <div className="min-h-screen bg-slate-50 flex flex-col">
//       <TopBar />

//       <main className="flex-1 px-4 py-4">
//         {tab === 0 && (
//           <HomeTab
//             deviceCount={deviceCount}
//             claimCount={claimCount}
//             purchases={purchases}
//             uniqueShopCount={uniqueShopCount}
//             latestPurchase={latestPurchase}
//             onGoDevices={() => setTab(1)}
//             onGoClaims={() => setTab(2)}
//             onOpenPurchases={() => openPurchaseList()}
//             onStartClaim={() => startClaim()}
//           />
//         )}

//         {/* TODO: Add your other tabs exactly like you had:
//             tab===1 <DevicesTab .../>
//             tab===2 <ClaimsTab .../>
//             tab===3 <ProfileTab .../>
//         */}
//       </main>

//       <BottomNav tab={tab} setTab={setTab} />

//       {/* TODO: Add your modals:
//           <PurchasesModal open={openPurchases} onClose={()=>setOpenPurchases(false)} .../>
//           <ClaimWizardModal .../>
//           <ClaimDetailsModal .../>
//           <AddressModal .../>
//           <Toast .../>
//       */}
//     </div>
//   );
// }


